import React, { useState, useEffect, useCallback } from 'react';
import { startOfMonth, endOfMonth, differenceInDays, format } from 'date-fns';
import {
  getServiceReports,
  getServiceReportSummary,
  getStaffList,
  exportServiceReportsCSV,
  downloadCSV,
  updatePaidAmount,
  createCashPayment,
  StaffServiceSummary,
} from '../../lib/services/serviceReportService';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

const ToastNotification: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    toast.type === 'success'
      ? 'bg-green-500'
      : toast.type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';

  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-4 text-white focus:outline-none">
        x
      </button>
    </div>
  );
};

const paymentTypeLabel = (type: string | null): string => {
  switch (type) {
    case 'card': return 'C';
    case 'wallet': return 'W';
    case 'cash': return 'Ca';
    case 'credit': return 'Cr';
    case 'pay_at_spa': return 'S';
    default: return '';
  }
};

const ServiceReports: React.FC = () => {
  // State
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));
  const [staffId, setStaffId] = useState<string>('');
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [reports, setReports] = useState<StaffServiceSummary[]>([]);
  const [summary, setSummary] = useState({
    totalAppointments: 0,
    totalRevenue: 0,
    totalTips: 0,
    avgTip: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedStaff, setExpandedStaff] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Expanded notes
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);

  // Inline editing for Paid column
  const [editingPaidId, setEditingPaidId] = useState<string | null>(null);
  const [editingPaidValue, setEditingPaidValue] = useState<string>('');
  const [isSavingPaid, setIsSavingPaid] = useState(false);

  // Toast management
  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Load staff list
  useEffect(() => {
    const loadStaff = async () => {
      const staff = await getStaffList();
      setStaffList(staff);
    };
    loadStaff();
  }, []);

  // Load reports when filters change
  const loadReports = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate date range (max 6 months)
      if (differenceInDays(endDate, startDate) > 180) {
        setError('Date range cannot exceed 6 months');
        setIsLoading(false);
        return;
      }

      if (endDate < startDate) {
        setError('End date must be after start date');
        setIsLoading(false);
        return;
      }

      const data = await getServiceReports(startDate, endDate, staffId || undefined);
      setReports(data);

      const summaryData = await getServiceReportSummary(startDate, endDate, staffId || undefined);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err.message || 'Failed to load service reports');
      addToast(`Failed to load reports: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, staffId, addToast]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Handle date changes with 6-month validation
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    setStartDate(date);

    if (differenceInDays(endDate, date) > 180) {
      const maxEndDate = new Date(date);
      maxEndDate.setDate(maxEndDate.getDate() + 180);
      setEndDate(maxEndDate);
      addToast('End date adjusted to 6-month limit', 'info');
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    setEndDate(date);

    if (differenceInDays(date, startDate) > 180) {
      const maxStartDate = new Date(date);
      maxStartDate.setDate(maxStartDate.getDate() - 180);
      setStartDate(maxStartDate);
      addToast('Start date adjusted to 6-month limit', 'info');
    }
  };

  // Quick date presets
  const setThisMonth = () => {
    setStartDate(startOfMonth(new Date()));
    setEndDate(endOfMonth(new Date()));
  };

  const setLastMonth = () => {
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    setStartDate(startOfMonth(lastMonth));
    setEndDate(endOfMonth(lastMonth));
  };

  const setThisWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const start = new Date(now);
    start.setDate(now.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    setStartDate(start);
    setEndDate(new Date());
  };

  const setLastWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const start = new Date(now);
    start.setDate(now.getDate() - dayOfWeek - 7);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    setStartDate(start);
    setEndDate(end);
  };

  // Toggle staff expansion
  const toggleStaffExpand = (staffId: string) => {
    setExpandedStaff((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(staffId)) {
        newSet.delete(staffId);
      } else {
        newSet.add(staffId);
      }
      return newSet;
    });
  };

  // Export CSV
  const handleExportCSV = () => {
    try {
      const csvContent = exportServiceReportsCSV(reports);
      const filename = `ServiceReports_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}.csv`;
      downloadCSV(csvContent, filename);
      addToast('Report exported to CSV', 'success');
    } catch (err: any) {
      addToast(`Failed to export CSV: ${err.message}`, 'error');
    }
  };

  // Inline paid editing
  const handleStartEditPaid = (aptId: string, currentAmount: number | null) => {
    setEditingPaidId(aptId);
    setEditingPaidValue(currentAmount != null ? currentAmount.toFixed(2) : '');
  };

  const handleCancelEditPaid = () => {
    setEditingPaidId(null);
    setEditingPaidValue('');
  };

  const handleSavePaid = async (apt: { id: string; payment_id: string | null }) => {
    const amount = parseFloat(editingPaidValue);
    if (isNaN(amount) || amount < 0) {
      addToast('Please enter a valid amount', 'error');
      return;
    }

    setIsSavingPaid(true);
    try {
      if (apt.payment_id) {
        await updatePaidAmount(apt.payment_id, amount);
      } else {
        await createCashPayment(apt.id, amount);
      }
      addToast('Paid amount updated', 'success');
      setEditingPaidId(null);
      setEditingPaidValue('');
      await loadReports();
    } catch (err: any) {
      addToast(`Failed to update: ${err.message}`, 'error');
    } finally {
      setIsSavingPaid(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Reports</h1>
          <p className="text-sm text-gray-600 mt-1">
            View paid services and tips by staff member
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MessageNotificationBell />
          <NotificationBell />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={format(startDate, 'yyyy-MM-dd')}
              onChange={handleStartDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={format(endDate, 'yyyy-MM-dd')}
              onChange={handleEndDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
            />
          </div>

          {/* Staff Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
            <select
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
            >
              <option value="">All Staff</option>
              {staffList.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <div className="flex items-end">
            <button
              onClick={handleExportCSV}
              disabled={reports.length === 0}
              className="w-full px-4 py-2 bg-[#3A5E63] text-white rounded-md hover:bg-[#2d4a4e] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Quick Presets */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={setThisWeek}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            This Week
          </button>
          <button
            onClick={setLastWeek}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Last Week
          </button>
          <button
            onClick={setThisMonth}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            This Month
          </button>
          <button
            onClick={setLastMonth}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Last Month
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow p-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3A5E63]"></div>
            <span className="ml-4 text-gray-600">Loading service reports...</span>
          </div>
        </div>
      )}

      {/* Data Display */}
      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Total Appointments</div>
              <div className="text-2xl font-bold text-[#3A5E63] mt-1">{summary.totalAppointments}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Total Revenue</div>
              <div className="text-2xl font-bold text-[#3A5E63] mt-1">${summary.totalRevenue.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Total Tips</div>
              <div className="text-2xl font-bold text-green-600 mt-1">${summary.totalTips.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Average Tip</div>
              <div className="text-2xl font-bold text-green-600 mt-1">${summary.avgTip.toFixed(2)}</div>
            </div>
          </div>

          {/* Staff Reports Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Services by Staff Member</h2>
            </div>

            {reports.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No paid services found for this period
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {reports.map((staffReport) => (
                  <div key={staffReport.staff_id}>
                    {/* Staff Header Row */}
                    <div
                      className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleStaffExpand(staffReport.staff_id)}
                    >
                      <div className="flex items-center gap-4">
                        {/* Staff Photo */}
                        {staffReport.staff_photo ? (
                          <img
                            src={staffReport.staff_photo}
                            alt={staffReport.staff_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#3A5E63] text-white flex items-center justify-center font-medium">
                            {staffReport.staff_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{staffReport.staff_name}</div>
                          <div className="text-sm text-gray-500">
                            {staffReport.total_appointments} {staffReport.total_appointments === 1 ? 'appointment' : 'appointments'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Revenue</div>
                          <div className="font-semibold text-gray-900">${staffReport.total_revenue.toFixed(2)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Paid</div>
                          <div className="font-semibold text-blue-600">${staffReport.total_paid.toFixed(2)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Tips</div>
                          <div className={`font-semibold ${staffReport.total_tips > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                            ${staffReport.total_tips.toFixed(2)}
                          </div>
                        </div>
                        <span className="text-gray-400">
                          {expandedStaff.has(staffReport.staff_id) ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {/* Expanded Appointments */}
                    {expandedStaff.has(staffReport.staff_id) && (
                      <div className="bg-gray-50 border-t border-gray-200">
                        <table className="min-w-full">
                          <thead>
                            <tr className="text-xs text-gray-500 uppercase">
                              <th className="px-6 py-3 text-left">Date</th>
                              <th className="px-6 py-3 text-left">Customer</th>
                              <th className="px-6 py-3 text-left">Services</th>
                              <th className="px-6 py-3 text-left">Notes</th>
                              <th className="px-6 py-3 text-right">Price</th>
                              <th className="px-6 py-3 text-right">Paid</th>
                              <th className="px-6 py-3 text-right">Tip</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {staffReport.appointments.map((apt) => (
                              <tr key={apt.id} className="hover:bg-gray-100">
                                <td className="px-6 py-3 text-sm">
                                  <div>{apt.appointment_date}</div>
                                  <div className="text-xs text-gray-400">
                                    {format(new Date(apt.start_time), 'h:mm a')}
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-sm">
                                  <div className="font-medium">{apt.customer_name}</div>
                                  {apt.customer_email && (
                                    <div className="text-xs text-gray-400">{apt.customer_email}</div>
                                  )}
                                </td>
                                <td className="px-6 py-3 text-sm">
                                  {apt.services.map((s) => s.name).join(', ')}
                                </td>
                                <td className="px-6 py-3 text-sm max-w-[250px]">
                                  {apt.crew_notes ? (
                                    <span
                                      className="text-gray-700 cursor-pointer hover:text-gray-900"
                                      onClick={() => setExpandedNoteId(expandedNoteId === apt.id ? null : apt.id)}
                                    >
                                      {expandedNoteId === apt.id ? (
                                        <span className="whitespace-pre-wrap break-words">{apt.crew_notes}</span>
                                      ) : (
                                        <span className="block truncate">
                                          {apt.crew_notes.length > 50
                                            ? apt.crew_notes.substring(0, 50) + '...'
                                            : apt.crew_notes}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-3 text-sm text-right">
                                  ${apt.total_price.toFixed(2)}
                                </td>
                                <td className="px-6 py-3 text-sm text-right">
                                  {editingPaidId === apt.id ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="text-gray-500">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editingPaidValue}
                                        onChange={(e) => setEditingPaidValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSavePaid(apt);
                                          if (e.key === 'Escape') handleCancelEditPaid();
                                        }}
                                        disabled={isSavingPaid}
                                        className="w-20 px-1 py-0.5 text-sm text-right border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleSavePaid(apt)}
                                        disabled={isSavingPaid}
                                        className="text-green-600 hover:text-green-800 text-xs font-medium"
                                      >
                                        {isSavingPaid ? '...' : '✓'}
                                      </button>
                                      <button
                                        onClick={handleCancelEditPaid}
                                        disabled={isSavingPaid}
                                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ) : (
                                    <span
                                      className="cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1 py-0.5 rounded transition-colors"
                                      onClick={() => handleStartEditPaid(apt.id, apt.paid_amount)}
                                      title="Click to edit"
                                    >
                                      {apt.paid_amount != null ? (
                                        <span className="text-blue-600 font-medium">
                                          ${apt.paid_amount.toFixed(2)}
                                          {apt.payment_type && (
                                            <span className="text-xs ml-1 text-gray-500">
                                              ({paymentTypeLabel(apt.payment_type)})
                                            </span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-3 text-sm text-right">
                                  {apt.tip_amount > 0 ? (
                                    <span className="text-green-600 font-medium">
                                      +${apt.tip_amount.toFixed(2)}
                                      {apt.tip_percentage && (
                                        <span className="text-xs ml-1">({apt.tip_percentage}%)</span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <ToastNotification key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </div>
  );
};

export default ServiceReports;
